/**
 * Inspection Service
 *
 * Provides CRUD operations and sub-resource management for inspections — quality
 * control visits performed at customer locations. Inspections follow a specific
 * workflow: pending -> in_progress -> observations_complete -> corrections_in_progress
 * -> completed. Inspections are SEPARATE from work orders and never mixed.
 *
 * Backend endpoints:
 *	 GET	/api/inspections						   — List/filter inspections
 *	 GET	/api/inspections/:id					   — Get a single inspection
 *	 POST	/api/inspections						   — Create a new inspection
 *	 PATCH	/api/inspections/:id					   — Update an existing inspection
 *	 DELETE /api/inspections/:id					   — Soft-delete an inspection
 *	 POST	/api/inspections/:id/photos				   — Add a photo (observation or correction)
 *	 DELETE /api/inspections/:id/photos/:photoId	   — Delete a photo
 *	 GET	/api/inspections/:id/pdf?type=			   — Generate PDF report
 *
 * All endpoints require authentication (Sanctum Bearer token).
 */

import { api } from './api';
import type { NotifyDecision } from './notificationService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Inspection statuses representing the full lifecycle.
 *
 * Lifecycle flow:
 *	 pending -> in_progress -> observations_complete -> corrections_in_progress -> completed
 *	 (any status) -> cancelled
 */
export type InspectionStatus =
	| 'pending'
	| 'in_progress'
	| 'observations_complete'
	| 'corrections_in_progress'
	| 'completed'
	| 'cancelled';

/**
 * Represents a photo attached to an inspection.
 * Photos are categorized as either observation photos (taken during inspection)
 * or correction photos (taken after deficiencies are fixed).
 *
 * @property id			- Unique photo identifier (UUID)
 * @property url		- Full URL to the stored image file
 * @property caption	- Optional description of what the photo shows
 * @property photoType	- 'observation' for initial findings, 'correction' for fixes
 * @property createdAt	- ISO timestamp of when the photo was uploaded
 */
export interface InspectionPhoto {
	id: string;
	url: string;
	/** Absolute URL of the stored image. Some payloads use `url`, newer ones
	 *  photoUrl — both are read where photos are rendered. */
	photoUrl?: string;
	/** Short label shown above the description in the gallery and the PDF. */
	title?: string | null;
	/** The photo's description. */
	caption: string;
	/** Area name as recorded at capture time. */
	area?: string | null;
	/** The CMS-managed area picked, when there was one. */
	areaId?: number | null;
	/** Ordering within the inspection. */
	sortOrder?: number;
	photoType: 'observation' | 'correction';
	createdAt: string;
}

/**
 * A work order spun out of an inspection via Create Follow-Up, as the detail
 * endpoint eager-loads it: enough to list it with a status and a link.
 * Remediation for a failed inspection lives on these, so the detail page
 * shows them before a reviewer completes the inspection.
 */
export interface InspectionFollowUpWorkOrder {
	id: string | number;
	projectName?: string | null;
	status: string;
	assignee?: {
		id: string | number;
		firstName?: string;
		lastName?: string;
		first_name?: string;
		last_name?: string;
	} | null;
	scheduledDate?: string | null;
	completionDate?: string | null;
}

/**
 * Represents an inspection as returned by the API.
 *
 * @property id				  - Unique identifier (UUID)
 * @property buildingName	  - Display name of the building/location being inspected
 * @property locationId		  - UUID of the location record
 * @property companyId		  - UUID of the customer company
 * @property companyName	  - Display name of the customer company (eager-loaded)
 * @property status			  - Current lifecycle status
 * @property inspectionDate	  - ISO date when inspection is/was performed
 * @property completedDate	  - ISO date when inspection was completed, or null
 * @property inspectorId	  - UUID of the user performing the inspection
 * @property inspectorName	  - Display name of the inspector (eager-loaded)
 * @property assigneeId		  - UUID of the technician assigned to fix deficiencies, or null
 * @property assigneeName	  - Display name of the assignee (eager-loaded), or null
 * @property contactName	  - On-site contact person name
 * @property generalNotes	  - Free-text notes and observations from the inspector
 * @property photos			  - Array of attached photos (eager-loaded)
 * @property createdAt		  - ISO timestamp of when the inspection was created
 * @property updatedAt		  - ISO timestamp of the last modification
 */
export interface Inspection {
	id: string;
	buildingName: string;
	locationId: string;
	companyId: string;
	companyName: string;
	status: InspectionStatus;
	inspectionDate: string;
	/** Optional start time in UTC ISO 8601 format */
	startTime: string | null;
	/** Optional end time in UTC ISO 8601 format */
	endTime: string | null;
	completedDate: string | null;
	inspectorId: string;
	inspectorName: string;
	assigneeId: string | null;
	assigneeName: string | null;
	contactName: string;
	/** Phone for the on-site contact (added in the work-order parity refactor). */
	contactPhone?: string | null;
	/** Email for the on-site contact (added in the work-order parity refactor). */
	contactEmail?: string | null;
	/** FK to a Contact record when the on-site rep is in the customer's contact list. */
	contactId?: number | null;
	/** Project address override (where the inspection occurs). */
	projectAddress?: string | null;
	projectAddressLine2?: string | null;
	/** Priority level — same enumerated set as work orders. */
	priority?: 'low' | 'medium' | 'high' | 'urgent' | null;
	/** Additional assignees beyond the primary one (eager-loaded by the API).
	 *  `role` lets the detail page render a "Subcontractor" badge when an
	 *  external vendor is assigned. */
	assignees?: Array<{
		id: string | number;
		firstName?: string;
		lastName?: string;
		first_name?: string;
		last_name?: string;
		role?: string;
		role_names?: string[];
		pivot?: { is_primary?: boolean | number };
	}>;
	/** Compliance gate — true means at least one photo is required to complete. */
	photosRequired?: boolean;
	/** Compliance gate — true means a customer signature is required to complete. */
	signatureRequired?: boolean;
	/**
	 * "Send to Client" — emails the finished inspection to the customer
	 * contact when it is completed. Defaults false server-side: a poor
	 * inspection is reviewed internally before the property manager sees it.
	 */
	notifyClient?: boolean;
	/** Recurring schedule (Daily, Weekly, etc.) — empty for one-off inspections.
	 *  Legacy field; new recurring inspections live on recurrence_series and
	 *  carry a recurrenceSeriesId back-link instead. */
	frequency?: string | null;
	/** Recurrence series ID — set when this inspection was generated by a
	 *  Google-Calendar-style recurring schedule. Drives the RecurrenceBadge
	 *  on list rows and the detail-page header. */
	recurrenceSeriesId?: number | null;
	generalNotes: string;
	/**
	 * Free-form summary surfaced beneath the per-item ratings on the
	 * inspection form. Distinct from generalNotes so the PDF report
	 * can address each section independently.
	 */
	recommendationsRemarks: string | null;
	/**
	 * Free-form follow-up notes surfaced beneath Recommendations.
	 * Often empty for clean inspections.
	 */
	followUpNotes: string | null;
	photos: InspectionPhoto[];
	/** Follow-up work orders (eager-loaded by the detail endpoint, newest first). */
	followUpWorkOrders?: InspectionFollowUpWorkOrder[];

	createdAt: string;
	updatedAt: string;
}

/**
 * Payload for creating a new inspection.
 * Status defaults to "pending" on the backend.
 *
 * The shape mirrors CreateWorkOrderData where it makes sense — the
 * inspection create form exposes the same Customer & Location,
 * Customer Contact, Assignment, and Settings sections, and each
 * section's fields round-trip through this interface. Billing and
 * pricing are deliberately absent: inspections carry neither, so there
 * is no Billing section and no estimate on the form, and nothing of
 * either to send.
 *
 * Extends NotifyDecision: an admin's answer to the "Notify users" prompt
 * (notifyUsers / notifyEmails) travels on the save itself, since who gets
 * emailed is decided by the same request that triggers the emails. Omitted by
 * every other caller, which leaves the server's own audience rules in charge.
 */
export interface CreateInspectionData extends NotifyDecision {
	/* ---- Customer & Location ---- */
	companyId: string;
	locationId: string;
	/** Optional override of the location's address for this inspection. */
	projectAddress?: string;
	/** Suite, unit, floor, etc. */
	projectAddressLine2?: string;

	/* ---- Customer Contact ---- */
	/** Display name of the on-site contact (auto-filled or manual).
	 *  Explicit null (edit mode) clears the stored snapshot. */
	contactName?: string | null;
	/** Phone (auto-filled when contactId is set). */
	contactPhone?: string | null;
	/** Email (auto-filled when contactId is set). */
	contactEmail?: string | null;
	/** FK to a Contact record when picked from the company's contact list.
	 *  Explicit null (edit mode) unlinks the contact — an absent key would
	 *  be ignored by the PATCH validator and keep the old link. */
	contactId?: number | null;

	/* ---- Assignment ---- */
	assigneeId?: string;
	/**
	 * Additional assignees beyond the primary one. Stored in the
	 * inspection_assignees junction table on the backend.
	 */
	additionalAssigneeIds?: string[];
	/** Priority level — same enumerated set as work orders. */
	priority?: 'low' | 'medium' | 'high' | 'urgent';

	/* ---- Inspection details ---- */
	inspectionDate: string;
	/** Optional start time in UTC ISO 8601 format */
	startTime?: string;
	/** Optional end time in UTC ISO 8601 format */
	endTime?: string;
	generalNotes?: string;

	/* ---- Settings ---- */
	/** Gate completion until at least one photo is attached. */
	photosRequired?: boolean;
	/** Gate completion until a customer signature is captured. */
	signatureRequired?: boolean;
	/**
	 * "Send to Client" — emails the finished inspection to the customer
	 * contact when it is completed. Defaults false server-side: a poor
	 * inspection is reviewed internally before the property manager sees it.
	 */
	notifyClient?: boolean;
	/** Recurring schedule (Weekly, Monthly, etc.) — empty for one-off. */
	frequency?: string;

	/* No estimate fields: inspections aren't priced, so neither the
	   form nor these payloads carry a cost, a range, or an approval. */
}

/**
 * Payload for updating an existing inspection.
 * All fields are optional (PATCH semantics) — only provided fields are updated.
 */
export interface UpdateInspectionData extends Partial<CreateInspectionData> {
	status?: InspectionStatus;
	/** Bottom-of-form recommendations textarea (auto-grow, 3 rows). */
	recommendationsRemarks?: string | null;
	/** Bottom-of-form follow-up textarea (auto-grow, 1 row). */
	followUpNotes?: string | null;
	/**
	 * Captured customer signature for this inspection. Stored as a PNG
	 * data URL on the `signature_data` column. Set during the workflow
	 * sign-off step and surfaced on PDF reports.
	 */
	signatureData?: string | null;
}

/**
 * Payload for adding a photo to an inspection.
 */
export interface CreateInspectionPhotoData {
	url: string;
	caption?: string;
	photoType: 'observation' | 'correction';
}

/**
 * Paginated API response wrapper for list endpoints.
 * Mirrors Laravel's pagination format.
 */
export interface PaginatedInspectionResponse {
	data: Inspection[];
	current_page: number;
	last_page: number;
	per_page: number;
	total: number;
}

/* ============================================
 * RESPONSE NORMALIZATION
 * ============================================ */

/**
 * Flattens nested relation objects from the API response into the flat
 * properties expected by the Inspection interface.
 *
 * The backend returns eager-loaded relations as nested objects (e.g.,
 * { company: { name: "Acme" }, location: { name: "HQ" }, inspector: { ... } })
 * but the frontend interface expects flat properties (companyName, buildingName, inspectorName).
 *
 * Call this AFTER camelizeKeys has converted all keys from snake_case to camelCase.
 *
 * @param raw - A camelized inspection object with nested relation objects
 * @returns A normalized Inspection with flat relation fields
 */
export function normalizeInspection(raw: Record<string, unknown>): Inspection {
	const company = raw.company as Record<string, unknown> | null;
	const location = raw.location as Record<string, unknown> | null;
	const inspector = raw.inspector as Record<string, unknown> | null;
	const assignee = raw.assignee as Record<string, unknown> | null;

	return {
		...raw,
		/* Flatten company name from the nested company relation */
		companyName: (raw.companyName as string) || (company?.name as string) || '',
		/* Flatten location/building name from the nested location relation */
		buildingName: (raw.buildingName as string) || (location?.name as string) || '',
		/* Flatten inspector display name from the nested inspector relation */
		inspectorName: (raw.inspectorName as string) || (
			inspector
				? `${(inspector.firstName as string) || ''} ${(inspector.lastName as string) || ''}`.trim()
				: ''
		),
		/* Flatten assignee display name from the nested assignee relation */
		assigneeName: (raw.assigneeName as string) || (
			assignee
				? `${(assignee.firstName as string) || ''} ${(assignee.lastName as string) || ''}`.trim()
				: null
		),
	} as Inspection;
}

/* ============================================
 * SERVICE METHODS
 * ============================================ */

/**
 * Inspection service methods.
 * Each method maps to a specific backend API endpoint.
 */
export const inspectionService = {
	/**
	 * GET /api/inspections
	 * List inspections with optional query filters (status, building, search, date range).
	 * Returns paginated results.
	 */
	getAll: (params?: Record<string, string>) =>
		api.get<PaginatedInspectionResponse>('/inspections', params),

	/**
	 * GET /api/inspections/:id
	 * Fetch a single inspection by ID with all related data
	 * (photos, company, location, inspector, assignee).
	 */
	getById: (id: string) =>
		api.get<Inspection>(`/inspections/${id}`),

	/**
	 * POST /api/inspections
	 * Create a new inspection. Returns the created resource with all relations.
	 */
	create: (data: CreateInspectionData) =>
		api.post<Inspection>('/inspections', data),

	/**
	 * PATCH /api/inspections/:id
	 * Partially update an inspection (status transitions, notes, etc.).
	 */
	update: (id: string, data: UpdateInspectionData) =>
		api.patch<Inspection>(`/inspections/${id}`, data),

	/**
	 * DELETE /api/inspections/:id
	 * Soft-delete an inspection (can be restored by admin).
	 */
	delete: (id: string) =>
		api.delete<void>(`/inspections/${id}`),

	/**
	 * POST /api/inspections/:id/photos
	 * Add a photo to an inspection (observation or correction type).
	 * Returns the created photo record.
	 */
	addPhoto: (inspectionId: string, data: CreateInspectionPhotoData) =>
		api.post<InspectionPhoto>(`/inspections/${inspectionId}/photos`, data),

	/**
	 * DELETE /api/inspections/:id/photos/:photoId
	 * Remove a photo from an inspection.
	 */
	deletePhoto: (inspectionId: string, photoId: string) =>
		api.delete<void>(`/inspections/${inspectionId}/photos/${photoId}`),

	/**
	 * GET /api/inspections/:id/pdf?type=pre_correction|post_correction
	 * Generate a PDF report for the inspection.
	 * 'pre_correction' shows observations only; 'post_correction' includes corrections.
	 */
	generatePdf: (id: string, type: 'pre_correction' | 'post_correction') =>
		api.get<string>(`/inspections/${id}/pdf`, { type }),
};
